import CryptoJS from "crypto-js";
import config from "../config";
import toast from "react-hot-toast";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/en"; // Importing Spanish as an example

dayjs.extend(relativeTime);

export const removeLocalStorageToken = (navigate) => {
  if (localStorage.getItem(`${config.NAME_KEY}:token`)) {
    localStorage.setItem(`${config.NAME_KEY}:token`, null);
  }
  if (navigate) {
    navigate("/");
  }
};

export const setLocalStorageToken = (token) => {
  localStorage.setItem(
    `${config.NAME_KEY}:token`,
    CryptoJS.AES.encrypt(token, `${config.NAME_KEY}-token`).toString(),
  );
};

export const getLocalStorageToken = () => {
  const token = localStorage.getItem(`${config.NAME_KEY}:token`);
  if (token) {
    const bytes = CryptoJS.AES.decrypt(token, `${config.NAME_KEY}-token`);
    return bytes.toString(CryptoJS.enc.Utf8);
  }
  return false;
};
const logger = (arg) => {
  if (config.SERVER !== "production") {
    // eslint-disable-next-line
    console.log(arg);
  }
  return false;
};
export default logger;
export const throwErrorMsg = (err) => {
  if (err._server_messages) {
    const messages = JSON.parse(err._server_messages);
    let parsedMessage = JSON.parse(messages[0]).message;
    parsedMessage = parsedMessage.replace(/<[^>]*>/g, "");
    toast.error(`${parsedMessage || toast.error("Something went wrong")}`);
  } else if (err?.message?.message) {
    toast.error(
      `${err.message.message || toast.error("Something went wrong")}`,
    );
  } else {
    toast.error(`${err.message || toast.error("Something went wrong")}`);
  }
};
export const formatTime = (timestamp) => {
  const now = dayjs();
  const time = dayjs(timestamp);

  // Check if the difference is more than 24 hours
  if (now.diff(time, "hour") >= 20) {
    // Show date after 24 hours
    return time.format("DD MMM YYYY hh:mm "); // Customize the format as needed
  } else {
    // Show relative time like "1 minute ago" if within 24 hours
    return time.fromNow();
  }
};
export const getInitials = (email) => {
  const local = email.split("@")[0];
  const parts = local.split(/[._-]/);
  return parts.length >= 2
    ? (parts[0][0] + parts[1][0]).toUpperCase()
    : local.slice(0, 2).toUpperCase();
};
export const clearAllCookies = () => {
  const cookies = document.cookie.split(";");
  cookies.forEach((cookie) => {
    const name = cookie.split("=")[0].trim();
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  });
};
// Helper: Returns which option ID matches a given date string (YYYY-MM-DD)
export const getOptionIdFromDate = (dateStr) => {
  if (!dateStr) return null;

  const selected = dayjs(dateStr).startOf("day");
  const today = dayjs().startOf("day");
  const tomorrow = dayjs().add(1, "day").startOf("day");
  const endOfWeek = dayjs().endOf("week");

  if (selected.isSame(today)) return "today";
  if (selected.isSame(tomorrow)) return "tomorrow";
  if (selected.isAfter(today) && selected.isBefore(endOfWeek.add(1, "day"))) {
    return "this-week";
  }
  return "custom";
};
export const copyToClipboard = (text) => {
  navigator.clipboard.writeText(text);
  toast.success("Task ID copied!");
};
const getEmailColor = (email) => {
  let hash = 0;
  for (let i = 0; i < email.length; i++)
    hash = email.charCodeAt(i) + ((hash << 5) - hash);
  const colors = [
    "bg-blue-100 text-blue-700",
    "bg-indigo-100 text-indigo-700",
    "bg-violet-100 text-violet-700",
    "bg-rose-100 text-rose-700",
    "bg-orange-100 text-orange-700",
    "bg-teal-100 text-teal-700",
    "bg-cyan-100 text-cyan-700",
    "bg-slate-100 text-slate-700",
  ];
  return colors[Math.abs(hash) % colors.length];
};
export const AssigneeAvatar = ({ email, size = "sm", className = "" }) => {
  const sizeClasses = {
    xs: "w-6 h-6 text-[10px]",
    sm: "w-8 h-8 text-xs",
    md: "w-10 h-10 text-sm",
  };

  return (
    <div
      className={`${sizeClasses[size]} ${getEmailColor(email)} home-assignee-chip rounded-full flex items-center justify-center text-white font-semibold border-2 border-white  transition-transform hover:scale-105 hover:z-10 cursor-pointer ${className}`}
      title={email}
    >
      {getInitials(email)}
    </div>
  );
};
export const getBlobDuration = async (blob) => {
  const audioContext = new AudioContext();
  const arrayBuffer = await blob.arrayBuffer();
  const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

  const duration = audioBuffer.duration;
  audioContext.close(); // Important for performance
  return duration;
};
export const loadPrivateAudio = async (fileData, authToken) => {
  if (!fileData?.file_url || !authToken) {
    throw new Error("Missing file_url or authToken");
  }
  if (fileData?.file_url) {
    try {
      const res = await fetch(
        `${config.API_BASE_URL}/api/method/frappe.utils.file_manager.download_file?file_url=${encodeURIComponent(
          fileData.file_url,
        )}`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        },
      );

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      }

      const blob = await res.blob();
      const audioDuration = await getBlobDuration(blob);
      const audioSrc = URL.createObjectURL(blob);

      return {
        id: fileData.name || `audio-${Date.now()}`, // fallback if name is missing
        blob: blob,
        url: audioSrc,
        duration: audioDuration,
        isUploaded: true,
      };
    } catch (err) {
      console.error("Failed to load private audio:", err);
      throw err; // Re-throw so caller can handle error
    }
  }
};
export const STATUS_OPTIONS = [
  "Backlog",
  "To Do",
  "Doing",
  "To Test",
  "Testing",
  "Verified",
  "Done",
  "Completed",
  "Cancelled",
];
export const toDateOnly = (dateStr) => {
  if (!dateStr) return null;
  return dateStr.split("T")[0]; // Handle ISO strings
};

export const getToday = () => new Date().toISOString().split("T")[0];

export const compareDates = (date1, date2) => {
  if (!date1) return 1;

  const d1 = new Date(date1);
  const d2 = new Date(date2);

  // Normalize to midnight to ignore time differences
  d1.setHours(0, 0, 0, 0);
  d2.setHours(0, 0, 0, 0);

  return d1.getTime() - d2.getTime();
};
export const categorizeTasks = (tasks) => {
  const today = dayjs().format("YYYY-MM-DD"); // ✅ Local date

  return tasks.reduce(
    (acc, task) => {
      const isCompleted = ["Completed", "Closed"].includes(task.status);
      if (task.status !== "Done") {
        if (isCompleted) {
          acc.completed.push(task);
        } else {
          const dueDate = toDateOnly(task.exp_end_date); // ✅ Also local
          const comparison = compareDates(dueDate, today);

          if (comparison < 0 && dueDate) {
            acc.overdue.push(task); // dueDate < today
          } else if (comparison === 0) {
            acc.today.push(task); // dueDate === today ✅ FIXED
          } else {
            acc.upcoming.push(task); // dueDate > today OR null
          }
        }
      }
      return acc;
    },
    { overdue: [], today: [], upcoming: [], completed: [] },
  );
};
export const categorizeTasksWithDone = (tasks) => {
  const today = dayjs().format("YYYY-MM-DD"); // ✅ Local date

  return tasks.reduce(
    (acc, task) => {
      const isCompleted = ["Completed", "Closed"].includes(task.status);

      if (isCompleted) {
        acc.completed.push(task);
      } else {
        const dueDate = toDateOnly(task.exp_end_date); // ✅ Also local
        const comparison = compareDates(dueDate, today);

        if (comparison < 0 && dueDate) {
          acc.overdue.push(task); // dueDate < today
        } else if (comparison === 0) {
          acc.today.push(task); // dueDate === today ✅ FIXED
        } else if (task.status === "Done") {
          acc.done.push(task);
        } else {
          acc.upcoming.push(task); // dueDate > today OR null
        }
      }

      return acc;
    },
    { overdue: [], today: [], upcoming: [], completed: [] },
  );
};
